/**
 * @file Asset loading front-end.
 *
 * Almost every shape in the game is still procedural (GDD §20); the exception
 * is the weapon models, which are real `.glb` files. Both kinds arrive through
 * this class, so the loading state never learns what an asset actually is.
 *
 * Anything already in the cache is skipped, which matters because the loading
 * state runs again on every floor.
 */

import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';

export class AssetLoader {
  constructor() {
    /** @type {THREE.LoadingManager} */
    this.manager = new THREE.LoadingManager();
    /** @type {Map<string, any>} @private */
    this._cache = new Map();
    /** @type {number} Load progress, 0..1. */
    this.progress = 0;

    this.manager.onProgress = (_url, loaded, total) => {
      this.progress = total > 0 ? loaded / total : 1;
    };
  }

  /**
   * Loads the asset manifest. An empty manifest — or one whose entries are all
   * cached from a previous floor — resolves immediately, which keeps the
   * loading state's contract identical either way.
   *
   * @param {Array<{ key: string, url: string, type: 'texture' | 'model' }>} [manifest]
   *   Assets to fetch.
   * @returns {Promise<void>}
   */
  async load(manifest = []) {
    const pending = manifest.filter((item) => !this._cache.has(item.key));
    if (pending.length === 0) {
      this.progress = 1;
      return;
    }

    await Promise.all(pending.map((item) => this._loadOne(item)));
    this.progress = 1;
  }

  /**
   * @param {{ key: string, url: string, type: 'texture' | 'model' }} item Manifest entry.
   * @returns {Promise<void>}
   * @private
   */
  _loadOne(item) {
    return new Promise((resolve, reject) => {
      if (item.type === 'texture') {
        new THREE.TextureLoader(this.manager).load(
          item.url,
          (texture) => {
            texture.colorSpace = THREE.SRGBColorSpace;
            this._cache.set(item.key, texture);
            resolve();
          },
          undefined,
          reject
        );
        return;
      }

      if (item.type === 'model') {
        new GLTFLoader(this.manager).load(
          item.url,
          (gltf) => {
            // Only the scene graph is kept, not the whole parsed document —
            // except its animation clips, stashed on the scene itself (the
            // convention `AnimationMixer` and every loader other than glTF's
            // own already use `object.animations` for) since most models here
            // have none and dropping them would pin the whole file for nothing.
            gltf.scene.animations = gltf.animations;
            this._cache.set(item.key, gltf.scene);
            resolve();
          },
          undefined,
          reject
        );
        return;
      }

      reject(new TypeError(`Unsupported asset type "${item.type}".`));
    });
  }

  /**
   * @param {string} key Asset key from the manifest.
   * @returns {any} The loaded asset, or undefined.
   */
  get(key) {
    return this._cache.get(key);
  }

  /**
   * Disposes every cached asset. Models are scene graphs rather than single GPU
   * resources, so they are walked: their geometries and materials are the ones
   * every clone in the world shares.
   * @returns {void}
   */
  dispose() {
    for (const asset of this._cache.values()) {
      if (typeof asset?.traverse === 'function') {
        asset.traverse((node) => {
          node.geometry?.dispose?.();
          for (const material of [node.material].flat()) material?.dispose?.();
        });
        continue;
      }
      asset?.dispose?.();
    }
    this._cache.clear();
  }
}
